import { Component, Input, OnChanges } from '@angular/core';
import { SubmissionDetailsParams } from '../../submission-details.model';
import { SubmissionDetailsActionsService } from '../../actions/submission-details-actions.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DownloadRequirementDocumentData, DeleteRequirementDocumentData, SaveRequirementDocumentData } from '../../../../domain/models/submissions.model';
import { AppBarAction, AppPageService } from 'common';
import { finalize, tap } from 'rxjs/operators';
import { OfferDetailsFacade } from './offer-details.facade';
import { OfferDetailsActionType, OfferDetailsComponentData } from './offer-details.model';
import { OfferSubmissionAdapter } from './offer-details.data';

@UntilDestroy()
@Component({
    selector: 'ifbp-offer-details',
    templateUrl: './offer-details.component.html',
    styleUrls: ['./offer-details.component.scss'],
    providers: [OfferDetailsFacade, OfferSubmissionAdapter],
    standalone: false
})
export class OfferDetailsComponent implements OnChanges {
  viewData: OfferDetailsComponentData;
  isFirstInit: boolean = true;
  actionInProgress: boolean;

  @Input()
  params: SubmissionDetailsParams;

  constructor(
    private readonly facade: OfferDetailsFacade,
    private readonly actions: SubmissionDetailsActionsService,
    private readonly appPageService: AppPageService
  ) { }

  ngOnChanges(): void {
    this.initData();
    this.initActions();
  }

  private initData(): void {
    this.facade.getComponentData$(this.params.appId).pipe(untilDestroyed(this))
      .subscribe(data => {
        this.viewData = data;
        this.actions.markClosingDocumentsSectionAsInvalid(false);
        if (this.isFirstInit) {
          this.firstInitActions();
          this.isFirstInit = false;
        }
      });
  }

  private firstInitActions(): void {
    this.actions.markDataAsLoaded(true);
  }

  private initActions(): void {
    this.actions.deleteRequirementDocument$.pipe(untilDestroyed(this))
      .subscribe((data: DeleteRequirementDocumentData) => {
        this.facade.removeCheckoutRequirementDocument(<number>this.viewData.selectedOfferBundleId, data);
      })

    this.actions.downloadRequirementDocument$.pipe(untilDestroyed(this))
      .subscribe((data: DownloadRequirementDocumentData) => {
        this.facade.downloadCheckoutRequirementDocument(data);
      })

    this.actions.saveRequirementDocuments$.pipe(untilDestroyed(this))
      .subscribe((data: SaveRequirementDocumentData[]) => {
        this.facade.saveCheckoutRequirementDocuments(<number>this.viewData.selectedOfferBundleId, data)
      })

    this.actions.updateOfferBundleStatus$.pipe(untilDestroyed(this))
      .subscribe((data) => this.facade.updateOfferBundleStatus(data, this.viewData));
  }

  onAppBarActionInvoke(action: AppBarAction, appId: number): void {
    if (this.actionInProgress) {
      return;
    }
    if (action.id !== OfferDetailsActionType.SubmitForReview) {
      return;
    }

    this.submitForReview(appId);
  }

  private submitForReview(appId: number): void {
    if (!this.viewData.selectedOfferBundleId) {
      return;
    }

    if (!this.viewData.offerRequirementsData?.isRequirementsDataValid) {
      this.actions.markClosingDocumentsSectionAsInvalid(true);
      return;
    }

    this.actionInProgress = true;
    this.facade.submitForReview(appId, this.viewData.application.brokerStatus, this.viewData.advisorData)
      .pipe(
        tap(() => this.handleSuccess()),
        finalize(() => this.handleComplete()))
      .subscribe();
  }

  private handleSuccess(): void {
    this.appPageService.back();
  }

  private handleComplete(): void {
    this.actionInProgress = false;
  }
}
